import { useMemo, useCallback } from 'react'
import { getImage, getPersonName, useSlsStateContext } from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import useRecords from '../../../views/indexing/hooks/useRecords'

/**
 * Hook that manages the logic for the PersonsList component, including filtering and selection state.
 *
 * @param {Object} params - The hook parameters
 * @param {boolean} [params.loading=false] - Boolean indicating external loading state
 * @param {string} [params.filterValue=''] - String to filter persons by name
 * @returns {Object} The hook return values
 * @returns {Object} return.personsListProps - Props to be passed to PersonsList component
 * @returns {boolean} return.showBlankState - Boolean indicating if the blank state should be shown
 * @returns {boolean} return.showChangeHistory - Boolean indicating if change history should be shown
 */
export default function usePersonList({ loading = false, filterValue = '' }) {
  const zionSlsState = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { currentImageArk } = zionSlsState
  const personIdsOrderForImage = zionSlsState.personOrder?.find((po) => po.partitionKey === currentImageArk)
  const currentImage = getImage(zionSlsState.images, zionSlsState?.currentImageArk)

  const { allRecords, floatingPersons, noPeople, imageHasData, isJiapu } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    personIdsOrder: personIdsOrderForImage?.personIds,
    thumbnails,
  })

  const globalLoading = loading || zionSlsState.loading
  const showLoading = loading || (noPeople && zionSlsState.loading)

  // Filter persons by name
  const filterPersonsByName = useCallback(
    (persons) => {
      if (!filterValue.trim()) return persons
      const lowerFilter = filterValue.toLowerCase()
      return persons.filter((person) => {
        const name = getPersonName({ person })?.toLowerCase() || ''
        return name.includes(lowerFilter)
      })
    },
    [filterValue]
  )

  // Get all persons from records and filter them
  const filteredRecordsWithPersons = useMemo(() => {
    if (!allRecords) return []
    return allRecords.map((record) => {
      const persons =
        record?.subElements?.filter((sub) => sub?.elementType === 'PERSON' && sub?.partitionKey === currentImageArk) ||
        []
      const filteredPersons = filterPersonsByName(persons)
      return { record, persons: filteredPersons }
    })
  }, [allRecords, currentImageArk, filterPersonsByName])

  // Filter floating persons
  const filteredFloatingPersons = useMemo(() => {
    if (!floatingPersons) return []
    return filterPersonsByName(floatingPersons)
  }, [floatingPersons, filterPersonsByName])

  const hasRecords = currentImage?.subElements?.length > 0

  // Computed state flags
  const showBlankState = !globalLoading && (noPeople || !imageHasData || isJiapu)
  const showChangeHistory = !globalLoading && imageHasData && hasRecords && !isJiapu

  // Props to spread into PersonsList component
  const personsListProps = {
    filteredRecordsWithPersons,
    filteredFloatingPersons,
    showLoading,
    isJiapu,
  }

  return {
    personsListProps,
    showBlankState,
    showChangeHistory,
  }
}
